import { useCallback, useMemo } from 'react';
import { useLocation, useNavigation } from 'react-router';
import { useNavigate } from '@/hooks/use-navigate';
import { isSamePathnameListingDataNavigationPending } from '@/lib/plp-panel-url-state';
import { applyRefinesToSearchParams, computeNextRefines } from '@/components/eataly-plp-filters/lib/refine-url';
import {
    countUserRefinesFromUrl,
    countUserSelectedRefinements,
} from '@/components/eataly-plp-filters/lib/plp-refinement-count';
import { withCategorySiblingFilter } from '@/components/eataly-plp-filters/lib/category-subcategories';
import {
    PLP_BADGE_EXCLUDED_REFINEMENT_IDS,
    PLP_SYSTEM_REFINEMENT_IDS,
    type UsePlpRefinementsParams,
    type UsePlpRefinementsReturn,
} from '@/components/eataly-plp-filters/lib/types';
import { inferFilterUiType } from '@/components/eataly-plp-filters/lib/refinement-ui-registry';
import { useDataLayer } from '@/hooks/use-data-layer';
function getAnalyticsFilterType(attributeId: string): string {
    const normalizedId = attributeId.toLowerCase();
    if (normalizedId === 'pmid') return 'on sale';
    if (normalizedId.includes('price')) return 'price';
    if (normalizedId.includes('discount') || normalizedId.includes('promotion')) return 'discount';
    if (normalizedId === 'cgid' || normalizedId.includes('category') || normalizedId.includes('producttype')) {
        return 'category';
    }
    return attributeId;
}

function getAnalyticsFilterValue(attributeId: string, value: string): string | null {
    if (attributeId.toLowerCase() === 'pmid') {
        return null;
    }
    if (getAnalyticsFilterType(attributeId) !== 'price') {
        return value;
    }

    const numbers = value.match(/\d+(?:\.\d+)?/g);
    const maximum = numbers?.at(-1);
    return maximum ? `up to ${Math.floor(Number(maximum))}€` : value;
}

function isFreeDateFilter(attributeId: string): boolean {
    return /(^|[_-])(date|data)($|[_-])/i.test(attributeId);
}

export function usePlpRefinements({
    result,
    refine = [],
    disabled = false,
    excludeRefinementAttributeIds = [],
    categoryId,
    showCategoryFilter = false,
}: UsePlpRefinementsParams): UsePlpRefinementsReturn {
    const navigate = useNavigate();
    const dataLayer = useDataLayer();
    const location = useLocation();
    const navigation = useNavigation();
    const isPending = useMemo(
        () =>
            isSamePathnameListingDataNavigationPending({
                pathname: location.pathname,
                search: location.search,
                navigationState: navigation.state,
                navigationLocation: navigation.location,
            }),
        [location.pathname, location.search, navigation.location, navigation.state]
    );

    const effectiveRefines = useMemo(() => {
        if (navigation.location) {
            return new URLSearchParams(navigation.location.search).getAll('refine');
        }
        return refine;
    }, [navigation.location, refine]);

    const excludedAttributeIds = useMemo(
        () => new Set([...PLP_SYSTEM_REFINEMENT_IDS, ...excludeRefinementAttributeIds]),
        [excludeRefinementAttributeIds]
    );
    const badgeExcludedAttributeIds = useMemo(
        () => new Set([...PLP_BADGE_EXCLUDED_REFINEMENT_IDS, ...excludeRefinementAttributeIds]),
        [excludeRefinementAttributeIds]
    );

    const selectedFiltersCount = useMemo(() => {
        if (disabled) {
            return 0;
        }

        if (navigation.location) {
            const pendingRefines = new URLSearchParams(navigation.location.search).getAll('refine');
            return countUserRefinesFromUrl(pendingRefines, badgeExcludedAttributeIds);
        }

        return countUserSelectedRefinements(result.selectedRefinements, badgeExcludedAttributeIds);
    }, [disabled, badgeExcludedAttributeIds, navigation.location, result.selectedRefinements]);

    const navigateWithRefines = useCallback(
        (nextRefines: string[]) => {
            const params = new URLSearchParams(location.search);
            applyRefinesToSearchParams(params, nextRefines);
            const serialized = params.toString();
            void navigate({
                pathname: location.pathname,
                search: serialized ? `?${serialized}` : '',
            });
        },
        [location.pathname, location.search, navigate]
    );

    const hasActiveFilter = useCallback(
        (attributeId: string) =>
            effectiveRefines.some((refineValue) => {
                if (!refineValue.startsWith(`${attributeId}=`)) {
                    return false;
                }
                // The PLP always refines by the route category. That is not a user-selected sibling.
                if (attributeId === 'cgid' && categoryId && refineValue === `cgid=${categoryId}`) {
                    return false;
                }
                return true;
            }),
        [categoryId, effectiveRefines]
    );

    const isFilterSelected = useCallback(
        (attributeId: string, value: string) => effectiveRefines.includes(`${attributeId}=${value}`),
        [effectiveRefines]
    );

    const toggleFilter = useCallback(
        (attributeId: string, value: string) => {
            const refines = new URLSearchParams(location.search).getAll('refine');
            if (!isFreeDateFilter(attributeId)) {
                dataLayer.track({
                    event: 'filter_click',
                    filter_type: getAnalyticsFilterType(attributeId),
                    filter_action: refines.includes(`${attributeId}=${value}`) ? 'remove' : 'select',
                    filter_value: getAnalyticsFilterValue(attributeId, value),
                    event_type: 'ga4_ce',
                });
            }
            navigateWithRefines(computeNextRefines(refines, attributeId, value));
        },
        [dataLayer, location.search, navigateWithRefines]
    );

    const clearAllRefinements = useCallback(() => {
        new URLSearchParams(location.search)
            .getAll('refine')
            .filter((refinement) => refinement.includes('='))
            .forEach((refinement) => {
                const [filterType, ...filterValueParts] = refinement.split('=');
                if (!isFreeDateFilter(filterType)) {
                    dataLayer.track({
                        event: 'filter_click',
                        filter_type: getAnalyticsFilterType(filterType),
                        filter_action: 'remove',
                        filter_value: getAnalyticsFilterValue(filterType, filterValueParts.join('=')),
                        event_type: 'ga4_ce',
                    });
                }
            });
        navigateWithRefines([]);
    }, [dataLayer, location.search, navigateWithRefines]);

    const contextualRefinements = useMemo(() => {
        if (disabled) {
            return [];
        }

        const hasOnlyOneProduct = result?.total === 1;
        const baseRefinements = hasOnlyOneProduct
            ? result?.refinements?.filter((refinement) => result?.selectedRefinements?.[refinement.attributeId])
            : result?.refinements;

        return baseRefinements?.flatMap((refinement) => {
            if (excludedAttributeIds.has(refinement.attributeId)) {
                return [];
            }

            const visibleRefinement = withCategorySiblingFilter(refinement, { categoryId, showCategoryFilter });
            return visibleRefinement ? [visibleRefinement] : [];
        });
    }, [
        categoryId,
        excludedAttributeIds,
        result?.refinements,
        result?.selectedRefinements,
        result?.total,
        disabled,
        showCategoryFilter,
    ]);

    const hasUsableRefinements = useMemo(() => {
        if (!contextualRefinements || contextualRefinements.length === 0) {
            return false;
        }

        return contextualRefinements.some((refinement) => inferFilterUiType(refinement) !== 'skip');
    }, [contextualRefinements]);

    return {
        refinements: contextualRefinements ?? [],
        isPending,
        isFilterSelected,
        hasActiveFilter,
        toggleFilter,
        clearAllRefinements,
        hasUsableRefinements,
        selectedFiltersCount,
    };
}
